import { Link } from 'react-router-dom';

import { ApiError } from '../widget-api/http-client';
import { formatIsoDateDdMmYyyy } from '../widget-lib/format-date';

export type FutureBookingConflict = {
  booking_id: number;
  service_id: number;
  service_name: string;
  slot_id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
  branch_id: number;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Parses only the known 409 envelope; returns null for all other errors. */
export function futureBookingsFromApiError(err: unknown): FutureBookingConflict[] | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.errorCode !== 'SUBSCRIPTION_HAS_FUTURE_BOOKINGS') {
    return null;
  }
  const details = asRecord(err.details) ?? asRecord(asRecord(err.rawBody)?.details);
  const raw = details?.bookings;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const o = asRecord(entry);
      if (!o) return null;
      const bookingId = num(o.booking_id);
      if (!bookingId) return null;
      return {
        booking_id: bookingId,
        service_id: num(o.service_id),
        service_name: str(o.service_name),
        slot_id: num(o.slot_id),
        slot_date: str(o.slot_date),
        start_time: str(o.start_time),
        end_time: str(o.end_time),
        branch_id: num(o.branch_id)
      };
    })
    .filter((x): x is FutureBookingConflict => x != null);
}

function slotTime(start: string, end: string): string {
  const a = start.slice(0, 5);
  const b = end.slice(0, 5);
  return b ? `${a}—${b}` : a;
}

export function FutureBookingsNotice({
  bookings,
  bookingsHref
}: {
  bookings: FutureBookingConflict[];
  bookingsHref: string;
}) {
  return (
    <div
      className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3 text-sm text-[var(--text)]"
      data-testid="future-bookings-notice"
    >
      <p className="font-medium">Сначала отмените будущие записи</p>
      <p className="mt-1 text-[var(--text-muted)]">
        Заморозка недоступна, пока у абонемента есть активные будущие записи. Отмените их самостоятельно.
      </p>
      <ul className="mt-2 max-h-52 space-y-1.5 overflow-y-auto pr-1">
        {bookings.map((b) => (
          <li
            key={`${b.booking_id}-${b.slot_id}`}
            className="rounded-lg border border-[var(--border)] px-2.5 py-2"
          >
            <div className="font-medium break-words">{b.service_name || `Услуга #${b.service_id}`}</div>
            <div className="text-xs text-[var(--text-muted)]">
              {formatIsoDateDdMmYyyy(b.slot_date)} · {slotTime(b.start_time, b.end_time)}
            </div>
          </li>
        ))}
      </ul>
      <Link
        to={bookingsHref}
        className="mt-2 inline-flex text-sm font-medium text-[var(--accent)] underline underline-offset-2"
      >
        Перейти в «Мои записи»
      </Link>
    </div>
  );
}
